import React, { useState, useEffect } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline } from './sectionHelpers';

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  company: string;
  avatar_url?: string;
  rating?: number | string;
}

const DEFAULT_TESTIMONIALS: Testimonial[] = [
  {
    quote: 'This product changed everything for our team. Exceptional quality and outstanding support every step of the way.',
    author: 'Jane Smith',
    role: 'CEO',
    company: 'Example Co.',
    avatar_url: '',
    rating: '5',
  },
];

interface Props {
  section: SectionInstance;
}

const TestimonialsCarousel: React.FC<Props> = ({ section }) => {
  const c = section.controls;
  const raw = Array.isArray(c.testimonials) && (c.testimonials as Testimonial[]).length > 0
    ? (c.testimonials as Testimonial[])
    : DEFAULT_TESTIMONIALS;
  const testimonials = raw;
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const timer = setInterval(() => setActive(prev => (prev + 1) % testimonials.length), 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const t = testimonials[Math.min(active, testimonials.length - 1)];
  const rating = Math.min(parseInt(String(t.rating || 5), 10) || 5, 5);
  const stars = '★'.repeat(rating) + '☆'.repeat(5 - rating);

  return (
    <section className="sb-render sb-render-testimonials" style={{ padding: '80px 24px', background: 'var(--brand-bg-alt, #f8fafc)' }}>
      <div style={{ maxWidth: 640, margin: '0 auto', textAlign: 'center' }}>
        <InlineEditText
          tag="h2"
          controlKey="headline"
          style={{ fontSize: '2.5rem', letterSpacing: '-0.025em', marginBottom: 48 }}
        >
          {sfHeadline(c, 'What Our Clients Say')}
        </InlineEditText>

        <div style={{ fontSize: '4rem', lineHeight: 1, color: 'var(--brand-primary, #2563eb)', opacity: 0.15, fontFamily: 'Georgia, serif', marginBottom: -16 }}>&ldquo;</div>
        <div style={{ color: '#f59e0b', fontSize: '1.25rem', marginBottom: 16 }}>{stars}</div>

        <blockquote style={{
          fontSize: '1.25rem', fontStyle: 'italic', marginBottom: 24, lineHeight: 1.6,
          background: 'var(--brand-bg, #fff)', padding: '32px', borderRadius: 12,
          boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)',
        }}>
          &ldquo;{t.quote}&rdquo;
        </blockquote>

        {/* Avatar + author meta */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          {t.avatar_url ? (
            <img
              src={t.avatar_url}
              alt={t.author || 'Testimonial author'}
              style={{
                width: 56, height: 56, borderRadius: '50%', objectFit: 'cover',
                border: '3px solid var(--brand-primary, #2563eb)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              }}
            />
          ) : (
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'var(--brand-primary, #2563eb)',
              color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '1.25rem',
            }}>
              {t.author ? t.author.charAt(0).toUpperCase() : '?'}
            </div>
          )}
          <div>
            <strong>{t.author}</strong>
            <div style={{ fontSize: '0.875rem', color: 'var(--brand-text-light, #64748b)' }}>
              {t.role}{t.company ? `, ${t.company}` : ''}
            </div>
          </div>
        </div>

        {testimonials.length > 1 && (
          <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'center' }}>
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                style={{
                  width: 10, height: 10, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  background: i === active ? 'var(--brand-primary, #2563eb)' : 'rgba(0,0,0,0.2)',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsCarousel;
